import React, { useState, useEffect, useCallback } from 'react';
import { Button, Input, Select, Card, Statistic, Spin, Empty, Tabs, message } from 'antd';
import {
  ReloadOutlined, SearchOutlined, InboxOutlined,
  ClockCircleOutlined, EyeOutlined, CheckCircleOutlined,
  MessageOutlined, TeamOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/auth/AuthContext';
import ContactMessageTable from '../../components/admin/contact/ContactMessageTable';
import ContactMessageDetailModal from '../../components/admin/contact/ContactMessageDetailModal';
import ContactRequestTable from '../../components/admin/contact/ContactRequestTable';
import ContactRequestDetailModal from '../../components/admin/contact/ContactRequestDetailModal';
import contactApi from '../../api/contactApi';

const { Option } = Select;

const personaLabels = {
  company: 'Company',
  association: 'Association',
  startup: 'Startup',
  incubator: 'Incubator',
  investor: 'Investor',
  academia: 'Academia',
  consultant: 'Consultant',
};

const ContactSubmissions = () => {
  const { isAuthenticated } = useAuth();

  // Website "Contact Us" messages
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Company "Contact AID Office" requests
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [requestSearchTerm, setRequestSearchTerm] = useState('');
  const [requestStatusFilter, setRequestStatusFilter] = useState('all');
  const [requestPersonaFilter, setRequestPersonaFilter] = useState('all');
  const [availablePersonas, setAvailablePersonas] = useState([]);

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 1000 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await contactApi.getContactSubmissions(params);

      if (response && response.success) {
        const data = response.data || [];
        setSubmissions(Array.isArray(data) ? data : []);
      } else {
        setSubmissions([]);
        message.error('Failed to fetch contact submissions');
      }
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
      setSubmissions([]);
      message.error('Error loading contact submissions');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const params = { limit: 1000 };
      if (requestStatusFilter !== 'all') params.status = requestStatusFilter;
      if (requestPersonaFilter !== 'all') params.personaType = requestPersonaFilter;
      if (requestSearchTerm) params.search = requestSearchTerm;

      const response = await contactApi.getContactAIDRequests(params);

      if (response && response.success) {
        const data = response.data || [];
        setRequests(Array.isArray(data) ? data : []);
      } else {
        setRequests([]);
        message.error('Failed to fetch contact requests');
      }
    } catch (error) {
      console.error('Error fetching contact requests:', error);
      setRequests([]);
      message.error('Error loading contact requests');
    } finally {
      setRequestsLoading(false);
    }
  }, [requestStatusFilter, requestPersonaFilter, requestSearchTerm]);

  useEffect(() => {
    if (isAuthenticated) {
      loadSubmissions();
      loadRequests();
    }
  }, [loadSubmissions, loadRequests, isAuthenticated]);

  useEffect(() => {
    const loadAvailablePersonas = async () => {
      if (!isAuthenticated) return;
      try {
        const response = await contactApi.getContactAIDRequests({ limit: 1000 });
        if (response && response.success) {
          const data = Array.isArray(response.data) ? response.data : [];
          const uniquePersonas = Array.from(
            new Set(data.map(r => r.personaType).filter(Boolean))
          );
          setAvailablePersonas(uniquePersonas);
        }
      } catch (error) {
        console.error('Error fetching available personas:', error);
      }
    };
    loadAvailablePersonas();
  }, [isAuthenticated]);

  const openDetailModal = (submission) => {
    setSelectedSubmission(submission);
    setIsDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedSubmission(null);
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const response = await contactApi.updateContactMessageStatus(id, newStatus);
      if (response && response.success) {
        message.success('Status updated successfully');
        await loadSubmissions();
        closeDetailModal();
      } else {
        message.error('Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Error updating status');
    }
  };

  const openRequestModal = (request) => {
    setSelectedRequest(request);
    setIsRequestModalOpen(true);
  };

  const closeRequestModal = () => {
    setIsRequestModalOpen(false);
    setSelectedRequest(null);
  };

  const handleRequestUpdate = async (id, updates) => {
    try {
      const response = await contactApi.updateContactAIDRequest(id, updates);
      if (response && response.success) {
        message.success('Request updated successfully');
        await loadRequests();
        closeRequestModal();
      } else {
        message.error('Failed to update request');
      }
    } catch (error) {
      console.error('Error updating request:', error);
      message.error('Error updating request');
    }
  };

  const filteredSubmissions = Array.isArray(submissions)
    ? submissions.filter(submission => {
        const searchLower = searchTerm.toLowerCase();
        return (
          submission.fullName?.toLowerCase().includes(searchLower) ||
          submission.email?.toLowerCase().includes(searchLower) ||
          submission.phone?.includes(searchTerm)
        );
      })
    : [];

  const filteredRequests = Array.isArray(requests)
    ? requests.filter(request => {
        const searchLower = requestSearchTerm.toLowerCase();
        return (
          request.contactName?.toLowerCase().includes(searchLower) ||
          request.email?.toLowerCase().includes(searchLower) ||
          request.subject?.toLowerCase().includes(searchLower) ||
          request.companyId?.companyName?.toLowerCase().includes(searchLower)
        );
      })
    : [];

  const stats = {
    total: submissions.length,
    new: submissions.filter(s => s.status === 'new').length,
    read: submissions.filter(s => s.status === 'read').length,
    resolved: submissions.filter(s => s.status === 'resolved').length,
  };

  const requestStats = {
    total: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    inProgress: requests.filter(r => r.status === 'in-progress').length,
    resolved: requests.filter(r => r.status === 'resolved' || r.status === 'closed').length,
  };

  return (
    <>
      <div className="max-w-9xl mx-auto w-full" style={{ fontFamily: 'Rubik, sans-serif' }}>
        <div className="p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Rubik, sans-serif', color: '#470172' }}>
                Contact Submissions
              </h1>
              <p className="text-gray-600" style={{ fontFamily: 'Rubik, sans-serif' }}>
                Manage website contact messages and company Contact AID Office requests
              </p>
            </div>
          </div>

          <Tabs
            defaultActiveKey="messages"
            items={[
              {
                key: 'messages',
                label: (
                  <span>
                    <MessageOutlined /> Website Messages
                  </span>
                ),
                children: (
                  <>
                    <div className="flex justify-end mb-4">
                      <Button
                        icon={<ReloadOutlined spin={loading} />}
                        onClick={loadSubmissions}
                        disabled={loading}
                        size="large"
                        style={{
                          fontFamily: 'Rubik, sans-serif',
                          fontSize: '16px',
                          fontWeight: 400,
                          color: '#470172',
                          borderColor: '#470172',
                          height: 'auto',
                          padding: '10px 20px'
                        }}
                      >
                        Refresh
                      </Button>
                    </div>

                    {/* Search and Filters */}
                    <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Input
                          size="large"
                          placeholder="Search by name, email..."
                          prefix={<SearchOutlined style={{ color: '#470172' }} />}
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          style={{
                            fontFamily: 'Rubik, sans-serif',
                            fontSize: '16px',
                            fontWeight: 400,
                          }}
                        />
                      </div>
                      <div>
                        <Select
                          size="large"
                          value={statusFilter}
                          onChange={(value) => setStatusFilter(value)}
                          style={{
                            width: '100%',
                            fontFamily: 'Rubik, sans-serif',
                            fontSize: '16px',
                            fontWeight: 400,
                          }}
                        >
                          <Option value="all">All Status</Option>
                          <Option value="new">New</Option>
                          <Option value="read">Read</Option>
                          <Option value="resolved">Resolved</Option>
                        </Select>
                      </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                      <Card>
                        <Statistic
                          title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Total Submissions</span>}
                          value={stats.total}
                          prefix={<InboxOutlined style={{ color: '#470172' }} />}
                          valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
                        />
                      </Card>
                      <Card>
                        <Statistic
                          title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>New</span>}
                          value={stats.new}
                          prefix={<ClockCircleOutlined style={{ color: '#fa8c16' }} />}
                          valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#fa8c16' }}
                        />
                      </Card>
                      <Card>
                        <Statistic
                          title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Read</span>}
                          value={stats.read}
                          prefix={<EyeOutlined style={{ color: '#1890ff' }} />}
                          valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#1890ff' }}
                        />
                      </Card>
                      <Card>
                        <Statistic
                          title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Resolved</span>}
                          value={stats.resolved}
                          prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                          valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#52c41a' }}
                        />
                      </Card>
                    </div>

                    {/* Submissions Table */}
                    <Card
                      styles={{ body: { overflowX: 'auto' } }}
                      title={
                        <div>
                          <p style={{ fontFamily: 'Rubik, sans-serif', fontSize: '15px', fontWeight: 400, color: '#470172', margin: '4px 0 0 0', opacity: 0.7 }}>
                            List of all contact form submissions
                          </p>
                        </div>
                      }
                    >
                      {loading ? (
                        <div className="flex justify-center items-center p-8">
                          <Spin size="large" />
                        </div>
                      ) : filteredSubmissions.length > 0 ? (
                        <ContactMessageTable
                          submissions={filteredSubmissions}
                          onView={openDetailModal}
                        />
                      ) : (
                        <Empty
                          description={
                            <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
                              {searchTerm || statusFilter !== 'all'
                                ? 'No submissions found matching your filters'
                                : 'No contact submissions yet'}
                            </span>
                          }
                        />
                      )}
                    </Card>
                  </>
                ),
              },
              {
                key: 'requests',
                label: (
                  <span>
                    <TeamOutlined /> Contact AID Office
                  </span>
                ),
                children: (
                  <>
                    <div className="flex justify-end mb-4">
                      <Button
                        icon={<ReloadOutlined spin={requestsLoading} />}
                        onClick={loadRequests}
                        disabled={requestsLoading}
                        size="large"
                        style={{
                          fontFamily: 'Rubik, sans-serif',
                          fontSize: '16px',
                          fontWeight: 400,
                          color: '#470172',
                          borderColor: '#470172',
                          height: 'auto',
                          padding: '10px 20px'
                        }}
                      >
                        Refresh
                      </Button>
                    </div>

                    {/* Search and Filters */}
                    <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Input
                          size="large"
                          placeholder="Search by name, company, subject..."
                          prefix={<SearchOutlined style={{ color: '#470172' }} />}
                          value={requestSearchTerm}
                          onChange={(e) => setRequestSearchTerm(e.target.value)}
                          style={{
                            fontFamily: 'Rubik, sans-serif',
                            fontSize: '16px',
                            fontWeight: 400,
                          }}
                        />
                      </div>
                      <div>
                        <Select
                          size="large"
                          value={requestStatusFilter}
                          onChange={(value) => setRequestStatusFilter(value)}
                          style={{
                            width: '100%',
                            fontFamily: 'Rubik, sans-serif',
                            fontSize: '16px',
                            fontWeight: 400,
                          }}
                        >
                          <Option value="all">All Status</Option>
                          <Option value="pending">Pending</Option>
                          <Option value="in-progress">In Progress</Option>
                          <Option value="resolved">Resolved</Option>
                          <Option value="closed">Closed</Option>
                          <Option value="rejected">Rejected</Option>
                        </Select>
                      </div>
                      <div>
                        <Select
                          size="large"
                          value={requestPersonaFilter}
                          onChange={(value) => setRequestPersonaFilter(value)}
                          style={{
                            width: '100%',
                            fontFamily: 'Rubik, sans-serif',
                            fontSize: '16px',
                            fontWeight: 400,
                          }}
                        >
                          <Option value="all">All Personas</Option>
                          {availablePersonas.map((persona) => (
                            <Option key={persona} value={persona}>
                              {personaLabels[persona] || persona}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </div>

                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                      <Card>
                        <Statistic
                          title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Total Requests</span>}
                          value={requestStats.total}
                          prefix={<InboxOutlined style={{ color: '#470172' }} />}
                          valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#470172' }}
                        />
                      </Card>
                      <Card>
                        <Statistic
                          title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Pending</span>}
                          value={requestStats.pending}
                          prefix={<ClockCircleOutlined style={{ color: '#fa8c16' }} />}
                          valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#fa8c16' }}
                        />
                      </Card>
                      <Card>
                        <Statistic
                          title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>In Progress</span>}
                          value={requestStats.inProgress}
                          prefix={<EyeOutlined style={{ color: '#1890ff' }} />}
                          valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#1890ff' }}
                        />
                      </Card>
                      <Card>
                        <Statistic
                          title={<span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>Resolved</span>}
                          value={requestStats.resolved}
                          prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                          valueStyle={{ fontFamily: 'Rubik', fontSize: '28px', fontWeight: 400, color: '#52c41a' }}
                        />
                      </Card>
                    </div>

                    {/* Requests Table */}
                    <Card
                      styles={{ body: { overflowX: 'auto' } }}
                      title={
                        <div>
                          <p style={{ fontFamily: 'Rubik, sans-serif', fontSize: '15px', fontWeight: 400, color: '#470172', margin: '4px 0 0 0', opacity: 0.7 }}>
                            List of all "Contact AID Office" requests submitted by member companies
                          </p>
                        </div>
                      }
                    >
                      {requestsLoading ? (
                        <div className="flex justify-center items-center p-8">
                          <Spin size="large" />
                        </div>
                      ) : filteredRequests.length > 0 ? (
                        <ContactRequestTable
                          requests={filteredRequests}
                          onView={openRequestModal}
                        />
                      ) : (
                        <Empty
                          description={
                            <span style={{ fontFamily: 'Rubik, sans-serif', fontSize: '16px', fontWeight: 400, color: '#470172' }}>
                              {requestSearchTerm || requestStatusFilter !== 'all' || requestPersonaFilter !== 'all'
                                ? 'No requests found matching your filters'
                                : 'No contact requests yet'}
                            </span>
                          }
                        />
                      )}
                    </Card>
                  </>
                ),
              },
            ]}
          />
        </div>
      </div>

      {isDetailModalOpen && selectedSubmission && (
        <ContactMessageDetailModal
          submission={selectedSubmission}
          isOpen={isDetailModalOpen}
          onClose={closeDetailModal}
          onStatusUpdate={handleStatusUpdate}
        />
      )}

      {isRequestModalOpen && selectedRequest && (
        <ContactRequestDetailModal
          request={selectedRequest}
          isOpen={isRequestModalOpen}
          onClose={closeRequestModal}
          onStatusUpdate={handleRequestUpdate}
        />
      )}
    </>
  );
};

export default ContactSubmissions;
